<template>
  <div class="v2-collection-edit">
    <!-- Loading -->
    <div
      v-if="loading && !collection.name"
      class="v2-page-loading"
    >
      <v2-spinner
        size="lg"
        label="Loading..."
      />
    </div>

    <template v-else>
      <!-- Page Header -->
      <div class="v2-flex v2-items-center v2-mb-6">
        <h1 class="v2-heading-sm">
          {{ title }}
        </h1>
      </div>

      <!-- Progress Bar -->
      <div
        v-if="showProgress"
        class="v2-collection-edit__progress v2-mb-4"
      >
        <div class="v2-collection-edit__progress-bar">
          <div
            class="v2-collection-edit__progress-fill"
            :style="{ width: progressPercent + '%' }"
          />
        </div>
        <span class="v2-collection-edit__progress-text">
          {{ progressMessage || `Uploading... ${progressPercent}%` }}
        </span>
      </div>

      <!-- Two Column Grid -->
      <div
        class="v2-collection-edit__grid"
        :class="{ 'v2-collection-edit__grid--no-catalog': !isBrandCatalogueEnabled }"
      >
        <!-- Logo Upload Card -->
        <div class="v2-card v2-collection-edit__card v2-collection-edit__card--cover">
          <h3 class="v2-collection-edit__card-title">
            Cover Photo
          </h3>
          <div
            class="v2-collection-edit__logo-area"
            :class="{ 'v2-collection-edit__logo-area--has-preview': logoPreview }"
            @click="$refs.logoInput.click()"
            @dragover.prevent
            @drop.prevent="onDropCollectionLogo"
          >
            <img
              v-if="logoPreview"
              :src="logoPreview"
              alt="Collection logo"
              class="v2-collection-edit__logo-preview"
            >
            <div
              v-else
              class="v2-collection-edit__logo-placeholder"
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle
                  cx="12"
                  cy="13"
                  r="4"
                />
              </svg>
              <span class="v2-collection-edit__logo-label">
                Upload collection<br>cover photo
              </span>
            </div>
          </div>
          <input
            ref="logoInput"
            type="file"
            accept=".jpg,.png,.jpeg,.svg"
            hidden
            @change="onLogoFileSelected"
          >
        </div>

        <!-- Product Upload Card -->
        <div class="v2-card v2-collection-edit__card v2-collection-edit__card--mass">
          <h3 class="v2-collection-edit__card-title">
            Mass Upload Products
          </h3>

          <!-- Download Sample Sheet -->
          <v2-button
            variant="outline"
            block
            class="v2-mb-3"
            @click="onDownloadSampleSheetClick"
          >
            {{ isEdit ? 'Download Collection' : 'Sample Product Sheet' }}
          </v2-button>

          <!-- File Drop Zone -->
          <div
            v-if="!isDromoUpload"
            class="v2-collection-edit__drop-zone"
            :class="{ 'v2-collection-edit__drop-zone--has-file': productFile }"
            @dragover.prevent
            @drop.prevent="onDropProductFile"
            @click="$refs.productInput.click()"
          >
            <div
              v-if="productFile && productFile.name"
              class="v2-collection-edit__file-info"
            >
              <span class="v2-collection-edit__file-name">
                {{ textTruncate(productFile.name) }}
              </span>
              <button
                class="v2-collection-edit__file-clear"
                @click.stop="clearFile"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line
                    x1="18"
                    y1="6"
                    x2="6"
                    y2="18"
                  />
                  <line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                  />
                </svg>
              </button>
            </div>
            <div
              v-else
              class="v2-collection-edit__drop-label"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line
                  x1="12"
                  y1="3"
                  x2="12"
                  y2="15"
                />
              </svg>
              <span>Upload file (.xlsx, .zip)</span>
            </div>
          </div>
          <input
            ref="productInput"
            type="file"
            accept=".xlsx,.zip"
            hidden
            @change="onProductFileSelected"
          >
          <small
            v-if="errors.productsFile"
            class="v2-input__error"
          >{{ errors.productsFile }}</small>

          <!-- Dromo AI Upload -->
          <div
            v-if="enableDromoDemo && !isFileUpload"
            class="v2-mt-3"
          >
            <v2-button
              block
              @click="startDromo"
            >
              Upload products with AI (xlsx)
            </v2-button>
          </div>
          <p
            v-if="isDromoUploadDone"
            class="v2-collection-edit__upload-success"
          >
            File uploaded successfully
          </p>
        </div>

        <!-- Collection Detail Card -->
        <div class="v2-card v2-collection-edit__card v2-collection-edit__card--form">
          <form @submit.prevent="onSyncCollection">
            <div class="v2-collection-edit__form-stack">
              <!-- Collection Name -->
              <v2-input
                :value="collection.name"
                label="Collection Name *"
                placeholder="Collection Name"
                :error="errors.name"
                @input="collection.name = $event"
                @blur="validateField('name')"
              />

              <!-- Description -->
              <div class="v2-input-wrapper">
                <label class="v2-input__label">Description *</label>
                <textarea
                  class="v2-input v2-collection-edit__textarea"
                  :class="{ 'v2-input--error': errors.description }"
                  :value="collection.description"
                  placeholder="Enter Collection Description..."
                  rows="3"
                  @input="collection.description = $event.target.value"
                  @blur="validateField('description')"
                />
                <small
                  v-if="errors.description"
                  class="v2-input__error"
                >{{ errors.description }}</small>
              </div>

              <!-- Collection Type -->
              <div class="v2-collection-edit__field">
                <label class="v2-input__label">Collection's Products Availability *</label>
                <div class="v2-collection-edit__radio-group">
                  <label
                    v-for="option in collectionTypeOptions"
                    :key="option.LABEL"
                    class="v2-collection-edit__radio"
                    :class="{ 'v2-collection-edit__radio--active': collection.type === option.LABEL }"
                  >
                    <input
                      type="radio"
                      :value="option.LABEL"
                      :checked="collection.type === option.LABEL"
                      name="collectionType"
                      class="v2-collection-edit__radio-input"
                      @change="collection.type = option.LABEL"
                    >
                    <span class="v2-collection-edit__radio-dot" />
                    <span class="v2-collection-edit__radio-text">{{ option.TEXT }}</span>
                  </label>
                </div>
              </div>

              <!-- Dates Row -->
              <div class="v2-collection-edit__dates-row">
                <div class="v2-collection-edit__date-field">
                  <label class="v2-input__label">Publish Date *</label>
                  <flat-pickr
                    v-model="collection.startDateString"
                    class="v2-input"
                    :config="{ dateFormat: KP_DATE_FORMAT.CONFIG }"
                    :placeholder="collection.startDateString || KP_DATE_FORMAT.PLACEHOLDER"
                  />
                </div>
                <div class="v2-collection-edit__date-field">
                  <label class="v2-input__label">Order Deadline</label>
                  <flat-pickr
                    v-model="collection.endDateString"
                    class="v2-input"
                    :config="{ dateFormat: KP_DATE_FORMAT.CONFIG }"
                    :placeholder="collection.endDateString || KP_DATE_FORMAT.PLACEHOLDER"
                  />
                </div>
              </div>

              <!-- Delivery Window -->
              <div class="v2-collection-edit__field">
                <label class="v2-input__label">Estimated Delivery Window</label>
                <div class="v2-collection-edit__dates-row">
                  <div class="v2-collection-edit__date-field">
                    <flat-pickr
                      v-model="fromDate"
                      class="v2-input"
                      :config="VUE_FLATPICKR_MONTH_CONFIG"
                      :placeholder="fromDate || KP_MONTH_FORMAT.PLACEHOLDER"
                    />
                  </div>
                  <div class="v2-collection-edit__date-field">
                    <flat-pickr
                      v-model="toDate"
                      class="v2-input"
                      :config="VUE_FLATPICKR_MONTH_CONFIG"
                      :placeholder="toDate || KP_MONTH_FORMAT.PLACEHOLDER"
                    />
                  </div>
                </div>
              </div>

            </div>
          </form>
        </div>
        <!-- Brand Catalogue Card (KP-3919): both sections share one row.
             There was no catalogue-file UI on this page before KP-3919, so
             with the flag off the card is simply absent — that is the old
             design, not a degraded new one. -->
        <div
          v-if="isBrandCatalogueEnabled"
          class="v2-card v2-collection-edit__card v2-collection-edit__card--catalog"
        >
          <h3 class="v2-collection-edit__card-title">
            Brand Catalogue
          </h3>
          <p class="v2-collection-edit__card-hint">
            Share your own catalogue and supporting files with buyers. They
            can download each file, a selection, or everything as one ZIP
            from Download Collection.
          </p>
          <div class="v2-collection-edit__asset-grid">
            <!-- Catalogue files (multiple) -->
            <div class="v2-collection-edit__asset-section">
              <h4 class="v2-collection-edit__asset-title">
                Catalogue files
                <span
                  v-if="collection.brandCatalogFiles && collection.brandCatalogFiles.length"
                  class="v2-collection-edit__asset-count"
                >{{ collection.brandCatalogFiles.length }}</span>
              </h4>
              <ul
                v-if="collection.brandCatalogFiles && collection.brandCatalogFiles.length"
                class="v2-collection-edit__asset-list"
              >
                <li
                  v-for="(file, index) in collection.brandCatalogFiles"
                  :key="`catalog-${index}-${file.url}`"
                  class="v2-collection-edit__asset-row"
                >
                  <span class="v2-collection-edit__asset-ext">{{ fileExtension(file.name) }}</span>
                  <span class="v2-collection-edit__asset-name">{{ textTruncate(file.name) }}</span>
                  <button
                    type="button"
                    class="v2-collection-edit__file-clear"
                    :aria-label="`Remove ${file.name}`"
                    @click="removeBrandCatalogFile(index)"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <line
                        x1="18"
                        y1="6"
                        x2="6"
                        y2="18"
                      />
                      <line
                        x1="6"
                        y1="6"
                        x2="18"
                        y2="18"
                      />
                    </svg>
                  </button>
                </li>
              </ul>
              <brand-catalog-upload-row
                v-if="brandCatalogUpload.section === 'catalog'"
                :upload="brandCatalogUpload"
                @pause="pauseBrandCatalogUpload"
                @resume="resumeBrandCatalogUpload"
                @cancel="cancelBrandCatalogUpload"
              />
              <div
                class="v2-collection-edit__drop-zone v2-collection-edit__drop-zone--slim"
                @dragover.prevent
                @drop.prevent="onDropBrandCatalogFiles"
                @click="$refs.brandCatalogInput.click()"
              >
                <div class="v2-collection-edit__drop-label v2-collection-edit__drop-label--slim">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line
                      x1="12"
                      y1="3"
                      x2="12"
                      y2="15"
                    />
                  </svg>
                  <span>Add files — .pdf, .ppt, .pptx, .html</span>
                </div>
              </div>
              <input
                ref="brandCatalogInput"
                type="file"
                accept=".pdf,.ppt,.pptx,.html"
                multiple
                hidden
                @change="onBrandCatalogFilesSelected"
              >
            </div>

            <!-- Additional files (multiple) -->
            <div class="v2-collection-edit__asset-section">
              <h4 class="v2-collection-edit__asset-title">
                Additional files
                <span
                  v-if="collection.brandCatalogAddonFiles && collection.brandCatalogAddonFiles.length"
                  class="v2-collection-edit__asset-count"
                >{{ collection.brandCatalogAddonFiles.length }}</span>
              </h4>
              <ul
                v-if="collection.brandCatalogAddonFiles && collection.brandCatalogAddonFiles.length"
                class="v2-collection-edit__asset-list"
              >
                <li
                  v-for="(file, index) in collection.brandCatalogAddonFiles"
                  :key="`addon-${index}-${file.url}`"
                  class="v2-collection-edit__asset-row"
                >
                  <span class="v2-collection-edit__asset-ext">{{ fileExtension(file.name) }}</span>
                  <span class="v2-collection-edit__asset-name">{{ textTruncate(file.name) }}</span>
                  <button
                    type="button"
                    class="v2-collection-edit__file-clear"
                    :aria-label="`Remove ${file.name}`"
                    @click="removeBrandCatalogAddonFile(index)"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <line
                        x1="18"
                        y1="6"
                        x2="6"
                        y2="18"
                      />
                      <line
                        x1="6"
                        y1="6"
                        x2="18"
                        y2="18"
                      />
                    </svg>
                  </button>
                </li>
              </ul>
              <brand-catalog-upload-row
                v-if="brandCatalogUpload.section === 'addon'"
                :upload="brandCatalogUpload"
                @pause="pauseBrandCatalogUpload"
                @resume="resumeBrandCatalogUpload"
                @cancel="cancelBrandCatalogUpload"
              />
              <div
                class="v2-collection-edit__drop-zone v2-collection-edit__drop-zone--slim"
                @dragover.prevent
                @drop.prevent="onDropBrandCatalogAddonFiles"
                @click="$refs.brandCatalogAddonInput.click()"
              >
                <div class="v2-collection-edit__drop-label v2-collection-edit__drop-label--slim">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line
                      x1="12"
                      y1="3"
                      x2="12"
                      y2="15"
                    />
                  </svg>
                  <span>Add files — .ppt, .xls, .pdf, .csv, .zip, .html</span>
                </div>
              </div>
              <input
                ref="brandCatalogAddonInput"
                type="file"
                accept=".ppt,.pptx,.xls,.xlsx,.pdf,.csv,.zip,.html"
                multiple
                hidden
                @change="onBrandCatalogAddonFilesSelected"
              >
            </div>
          </div>
        </div>
      </div>

      <!-- Save island (KP-3919): appears only while the form holds unsaved
           changes; file uploads persist instantly and never summon it. -->
      <transition name="kp-island">
        <div
          v-if="hasUnsavedChanges"
          class="v2-collection-edit__save-island"
        >
          <span class="v2-collection-edit__save-island-text">
            <span class="v2-collection-edit__save-island-label">
              You've made changes to this collection
            </span>
            <span class="v2-collection-edit__save-island-sub">
              Save them so buyers see the latest version
            </span>
          </span>
          <v2-button
            size="lg"
            :disabled="disabled"
            :loading="loading"
            @click="onSyncCollection"
          >
            {{ buttonText }}
          </v2-button>
        </div>
      </transition>

      <!-- Crop Modal -->
      <v2-modal
        :show="!!croppedImg"
        title="Crop cover photo"
        size="lg"
        @close="closeCrop"
      >
        <div class="v2-collection-edit__cropper-section">
          <cropper
            ref="cropper"
            class="v2-collection-edit__cropper"
            :src="croppedImg"
            :stencil-props="{ aspectRatio: 1 / 1 }"
            image-restriction="fit-area"
          />
        </div>
        <template #footer>
          <v2-button
            variant="outline"
            @click="closeCrop"
          >
            Cancel
          </v2-button>
          <v2-button
            :loading="isCropping"
            @click="crop"
          >
            {{ isCropping ? 'Cropping...' : 'Crop' }}
          </v2-button>
        </template>
      </v2-modal>

      <!-- Order Update Disclaimer Modal -->
      <v2-modal
        :show="showOrderUpdateModal"
        title="Collection Update Disclaimer!"
        size="lg"
        @close="showOrderUpdateModal = false"
      >
        <p class="v2-collection-edit__order-message">
          Some SKUs with updated prices exist in orders from retailers. Would
          you like to update prices in not confirmed orders?
        </p>
        <div
          v-if="ordersToUpdate.length"
          class="v2-collection-edit__orders-list"
        >
          <div
            v-for="order in ordersToUpdate"
            :key="order._id || order.orderId"
            class="v2-collection-edit__order-item"
          >
            {{ order.orderNumber || order._id || order.orderId }}
          </div>
        </div>
        <template #footer>
          <v2-button
            variant="outline"
            @click="showOrderUpdateModal = false"
          >
            Cancel
          </v2-button>
          <v2-button
            variant="secondary"
            @click="onDoNotUpdateOrders"
          >
            Don't Update
          </v2-button>
          <v2-button @click="onUpdateOrders">
            Update Orders
          </v2-button>
        </template>
      </v2-modal>

      <!-- Download Collection Modal (edit mode) -->
      <!-- Pass the loaded collection (not just the id) so the modal can list
           the brand catalogue files it will include. -->
      <v2-download-catalog-modal
        :show="showDownloadModal"
        :collection="{ ...collection, _id: collectionId }"
        @close="showDownloadModal = false"
      />
    </template>
  </div>
</template>

<script>
import { useCollectionEdit } from '@/composables/useCollectionEdit'
import { useBrandCatalogueFlag } from '@/composables/useBrandCatalogueFlag'
import { VUE_FLATPICKR_MONTH_CONFIG } from '@/constants'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import flatPickr from 'vue-flatpickr-component'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import BrandCatalogUploadRow from '@/components/collection/BrandCatalogUploadRow.vue'
import V2DownloadCatalogModal from './V2DownloadCatalogModal.vue'

export default {
  name: 'V2CollectionEdit',
  components: {
    V2Button,
    V2Input,
    V2Modal,
    V2Spinner,
    flatPickr,
    Cropper,
    V2DownloadCatalogModal,
    BrandCatalogUploadRow,
  },
  setup() {
    const composable = useCollectionEdit(null)
    return { ...composable, ...useBrandCatalogueFlag() }
  },
  data() {
    return {
      saveBaseline: null,
      baselineSettleUntil: null,
      showProgress: false,
      progressPercent: 0,
      progressMessage: '',
      showOrderUpdateModal: false,
      showDownloadModal: false,
      VUE_FLATPICKR_MONTH_CONFIG,
    }
  },
  computed: {
    // The fields Save Changes actually commits (uploads persist on their
    // own). Serialized so one string compare answers "is anything unsaved?".
    editSnapshot() {
      const c = this.collection || {}
      return JSON.stringify([
        c.name,
        c.description,
        c.type,
        c.startDateString,
        c.endDateString,
        this.fromDate,
        this.toDate,
        c.logo,
        (c.brandCatalogFiles || []).map(file => file.url),
        (c.brandCatalogAddonFiles || []).map(file => file.url),
      ])
    },
    hasUnsavedChanges() {
      // img only exists mid-crop; the cropped cover waits in logoFile.
      if (this.img || this.logoFile || this.productFile) return true
      return this.saveBaseline !== null && this.editSnapshot !== this.saveBaseline
    },
    collectionTypeOptions() {
      return Object.values(this.COLLECTION_TYPE)
    },
  },
  watch: {
    // Inputs normalise their values for a beat after load (date pickers
    // reformat, watchers fill defaults) — inside the settle window the
    // baseline follows along, so only USER edits count as unsaved.
    editSnapshot(snap) {
      if (this.baselineSettleUntil && Date.now() < this.baselineSettleUntil) {
        this.saveBaseline = snap
      }
    },
    'collection._id': {
      handler(id) {
        if (id) this.openBaselineWindow()
      },
    },
    img: {
      handler(theFile) {
        if (!theFile) {
          // no-op: The composable watcher already sets croppedImg via toBase64
          // The crop modal opens reactively when croppedImg becomes truthy
        }
      },
    },
  },
  created() {
    this.collectionId = this.$route.params.id || null
    if (this.collectionId) {
      this.fetchCollection()
    } else {
      // Create mode: the pristine blank form is the baseline.
      this.openBaselineWindow()
    }
  },
  methods: {
    openBaselineWindow() {
      this.baselineSettleUntil = Date.now() + 2000
      this.saveBaseline = this.editSnapshot
      setTimeout(() => {
        if (this.baselineSettleUntil) {
          this.saveBaseline = this.editSnapshot
          this.baselineSettleUntil = null
        }
      }, 2000)
    },
    collectionCallbackOptions() {
      return {
        redirectRouteName: 'v2-collections',
        onSuccess: () => {
          this.showOrderUpdateModal = false
          // Saved — the current form state is the new baseline.
          this.saveBaseline = this.editSnapshot
        },
        onOrderConflict: () => {
          this.showOrderUpdateModal = true
        },
      }
    },
    progressCallback(data) {
      const fieldMap = {
        showProgress: 'showProgress',
        uploadPercentage: 'progressPercent',
        uploadMessage: 'progressMessage',
      }
      Object.entries(fieldMap).forEach(([dataKey, localKey]) => {
        if (data[dataKey] !== undefined) {
          this[localKey] = data[dataKey]
        }
      })
    },
    async onSyncCollection() {
      const isValid = this.validateAll()
      if (!isValid) return
      await this.syncCollection(this.progressCallback, this.$router, this.collectionCallbackOptions())
    },
    onDoNotUpdateOrders() {
      this.doNotUpdateOrders()
      this.updateCollection(this.progressCallback, this.$router, this.collectionCallbackOptions())
    },
    onUpdateOrders() {
      const orderIds = this.ordersToUpdate.map(o => o._id || o.orderId)
      this.updateOrdersPayload(orderIds)
      this.showOrderUpdateModal = false
      this.updateCollection(this.progressCallback, this.$router, this.collectionCallbackOptions())
    },
    onDownloadSampleSheetClick() {
      // In edit mode onDownloadSampleSheet invokes this callback (mirrors the
      // v1 page, which opened the download-catalog modal). Without it the
      // "Download Collection" button did nothing. In create mode the callback
      // is ignored and the sample sheet downloads directly.
      this.onDownloadSampleSheet(() => {
        this.showDownloadModal = true
      })
    },
    _handleFileInput(e, onFile) {
      const file = e.target.files[0]
      if (file) onFile(file)
      // Reset the input so the same file can be selected again
      e.target.value = ''
    },
    onLogoFileSelected(e) {
      this._handleFileInput(e, file => { this.img = file })
    },
    onProductFileSelected(e) {
      this._handleFileInput(e, file => this.handleFileUpload(file))
    },
    fileExtension(name) {
      const dot = (name || '').lastIndexOf('.')
      return dot > 0 ? name.slice(dot + 1).toUpperCase() : 'FILE'
    },
    onBrandCatalogFilesSelected(e) {
      this.uploadBrandCatalogFiles(e.target.files)
      e.target.value = ''
    },
    onBrandCatalogAddonFilesSelected(e) {
      this.uploadBrandCatalogAddonFiles(e.target.files)
      e.target.value = ''
    },
    onDropBrandCatalogFiles(e) {
      this.uploadBrandCatalogFiles(e.dataTransfer.files)
    },
    onDropBrandCatalogAddonFiles(e) {
      this.uploadBrandCatalogAddonFiles(e.dataTransfer.files)
    },
    closeCrop() {
      this.img = null
    },
    async crop() {
      await this.cropImage(this.$refs.cropper, () => {
        // croppedImg is cleared inside composable when img is set to null
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-collection-edit {
  padding-bottom: 120px;

  &__progress {
    // Glass card — progress banner above the form, lighter nested treatment.
    background: var(--v2-glass-surface-2);
    backdrop-filter: blur(var(--v2-glass-blur-sm)) saturate(160%);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    padding: var(--v2-space-3) var(--v2-space-4);
  }

  &__progress-bar {
    width: 100%;
    height: 8px;
    background: var(--v2-border);
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: var(--v2-space-2);
  }

  &__progress-fill {
    height: 100%;
    background: var(--v2-primary);
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  &__progress-text {
    font-family: var(--v2-font-sans);
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
  }

  // Room for the floating save island so the last card is never hidden.
  padding-bottom: 96px;

  // The commit action lives in a fixed bottom island — always in reach,
  // never buried mid-page above content that continues below it.
  &__save-island {
    position: fixed;
    left: 50%;
    bottom: 20px;
    transform: translateX(-50%);
    z-index: 40;
    display: flex;
    align-items: center;
    gap: 22px;
    padding: 12px 14px 12px 24px;
    // PLG's .glass-panel recipe, verbatim (pipelines bulk bar): a whisper
    // of dark tint + heavy blur reads as true glass over any content.
    border-radius: 1.25rem;
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.4),
      0 24px 48px -12px rgba(0, 0, 0, 0.18),
      0 4px 12px -4px rgba(0, 0, 0, 0.08);
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
  }

  &__save-island-text {
    display: flex;
    flex-direction: column;
    gap: 1px;

    @media (max-width: 640px) {
      display: none;
    }
  }

  &__save-island-label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    white-space: nowrap;
  }

  &__save-island-sub {
    font-family: var(--v2-font-sans);
    font-size: 0.75rem;
    color: var(--v2-muted-foreground);
    white-space: nowrap;
  }

  // Explicit 2x2 grid: row one (Cover / Detail) and row two (Mass Upload /
  // Brand Catalogue) share edges, so the four cards read as one aligned
  // composition instead of two drifting columns.
  &__grid {
    display: grid;
    grid-template-columns: 1fr 2fr;
    grid-template-areas:
      'cover form'
      'mass catalog';
    gap: var(--v2-space-6);
  }

  // Flag off: KP-3919 restructured this grid to make room for a fourth
  // card, so hiding that card would otherwise leave an empty quadrant
  // rather than the layout this page had before. Spanning the form down
  // the right column reproduces the original: uploads stacked left, form
  // beside them.
  &__grid--no-catalog {
    grid-template-areas:
      'cover form'
      'mass form';
  }

  &__card--cover {
    grid-area: cover;
    display: flex;
    flex-direction: column;
  }

  &__card--form {
    grid-area: form;
  }

  &__card--mass {
    grid-area: mass;
    align-self: start;
  }

  &__card--catalog {
    grid-area: catalog;
  }

  &__card {
    padding: var(--v2-space-6);
  }

  &__card-title {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0 0 var(--v2-space-4);
  }

  &__card-hint {
    font-family: var(--v2-font-sans);
    font-size: 0.8125rem;
    color: var(--v2-muted-foreground);
    margin: calc(-1 * var(--v2-space-2)) 0 var(--v2-space-4);
  }

  // Logo upload area
  &__logo-area {
    width: 100%;
    flex: 1;
    min-height: 320px;
    border: 2px dashed var(--v2-border);
    border-radius: var(--v2-radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    overflow: hidden;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: var(--v2-primary);
      background: var(--v2-muted);
    }

    &--has-preview {
      border-style: solid;
      border-color: var(--v2-border);

      &:hover {
        border-color: var(--v2-primary);
        background: transparent;
      }
    }
  }

  &__logo-preview {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__logo-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--v2-space-3);
    color: var(--v2-muted-foreground);
  }

  &__logo-label {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    text-align: center;
    line-height: 1.4;
  }

  // Brand catalogue asset sections (KP-3919, multiple files per section)
  &__asset-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-5);

    @media (max-width: 1100px) {
      grid-template-columns: 1fr;
    }
  }

  &__asset-section {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-2);
    min-width: 0;
  }

  &__asset-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--v2-font-sans);
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    margin: 0;
  }

  &__asset-count {
    font-size: 0.6875rem;
    font-weight: 700;
    line-height: 1;
    color: #6a73e8;
    background: rgba(116, 125, 238, 0.1);
    border-radius: 999px;
    padding: 3px 7px;
  }

  &__asset-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-2);
  }

  &__asset-row {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    padding: var(--v2-space-2) var(--v2-space-3);
    // Plasma-tinted surface — pure white rows fought the theme.
    background: #f6f5fc;
    border: 1px solid #e9e6f6;
    border-radius: var(--v2-radius-md);
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: rgba(116, 125, 238, 0.5);
      background: rgba(116, 125, 238, 0.09);
    }
  }

  &__asset-ext {
    flex-shrink: 0;
    font-family: var(--v2-font-sans);
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1;
    color: #7a7fd8;
    background: linear-gradient(
      160deg,
      rgba(116, 125, 238, 0.09),
      rgba(116, 125, 238, 0.03)
    );
    border: 1px solid rgba(255, 255, 255, 0.7);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.75);
    border-radius: 999px;
    padding: 4px 9px;
    min-width: 36px;
    text-align: center;
  }

  &__asset-name {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // Section-scoped upload loader (KP-3919): an incoming-file row that lives
  // exactly where the uploaded file will appear — orbiting ring, rising
  // arrow, shimmer sweep, indeterminate bar. Transform/opacity only.

  // Round icon controls: hover fills + lifts, :active presses in, and the
  // released press throws an expanding ring (::after) — pure CSS feedback.

  // File drop zone
  &__drop-zone {
    border: 2px dashed var(--v2-border);
    border-radius: var(--v2-radius-md);
    padding: var(--v2-space-4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    min-height: 64px;
    transition: border-color 0.15s ease, background 0.15s ease;

    &:hover {
      border-color: var(--v2-primary);
      background: var(--v2-muted);
    }

    &--has-file {
      border-style: solid;
      border-color: var(--v2-primary);
      background: var(--v2-muted);
    }

    &--compact {
      min-height: 48px;
      padding: var(--v2-space-3);
    }

    // Slim add-files control inside the asset sections — the files own the
    // space, the affordance stays one quiet line.
    &--slim {
      min-height: 38px;
      padding: 6px var(--v2-space-3);
      border-width: 1px;
    }
  }

  &__drop-label {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);

    &--slim {
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--v2-muted-foreground);
    }
  }

  &__file-info {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    width: 100%;
  }

  &__file-name {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--v2-foreground);
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__file-clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: var(--v2-space-1);
    background: transparent;
    border: none;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-muted-foreground);
    cursor: pointer;
    transition: all 0.15s ease;
    flex-shrink: 0;

    &:hover {
      background: var(--v2-destructive);
      color: #fff;
    }
  }

  &__upload-success {
    text-align: center;
    color: var(--v2-success);
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    font-weight: 600;
    margin-top: var(--v2-space-3);
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-success);
    border-radius: var(--v2-radius-md);
    background: rgba(16, 185, 129, 0.05);
  }

  // Form stack
  &__form-stack {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-4);
  }

  &__textarea {
    resize: vertical;
    min-height: 80px;
    font-family: var(--v2-font-sans);
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-1);
  }

  // Radio group
  &__radio-group {
    display: flex;
    gap: var(--v2-space-4);
    margin-top: var(--v2-space-1);
  }

  &__radio {
    display: flex;
    align-items: center;
    gap: var(--v2-space-2);
    cursor: pointer;
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    transition: all 0.15s ease;

    &:hover {
      border-color: var(--v2-primary);
    }

    &--active {
      border-color: var(--v2-primary);
      background: rgba(116, 125, 238, 0.05);
    }
  }

  &__radio-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  &__radio-dot {
    width: 16px;
    height: 16px;
    border: 2px solid var(--v2-border);
    border-radius: 50%;
    position: relative;
    flex-shrink: 0;
    transition: border-color 0.15s ease;

    .v2-collection-edit__radio--active & {
      border-color: var(--v2-primary);

      &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--v2-primary);
      }
    }
  }

  &__radio-text {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
  }

  // Dates row
  &__dates-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--v2-space-3);
  }

  &__date-field {
    display: flex;
    flex-direction: column;
    gap: var(--v2-space-1);

    ::v-deep .flatpickr-input {
      width: 100%;
    }
  }

  // Cropper
  &__cropper-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--v2-space-4);
  }

  &__cropper {
    max-height: 400px;
    width: 100%;
  }

  // Order update modal
  &__order-message {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    text-align: center;
    margin: 0 0 var(--v2-space-4);
  }

  &__orders-list {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    padding: var(--v2-space-2);
  }

  &__order-item {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    padding: var(--v2-space-2) var(--v2-space-3);
    color: var(--v2-foreground);
    border-bottom: 1px solid var(--v2-border);

    &:last-child {
      border-bottom: none;
    }
  }
}

// Shared utility classes (scoped)
// Tinted glass panel — matches V2Card variant="panel" recipe (no
// `backdrop-filter`; substrate-blur via LayoutV2::before mesh + inner
// top-edge highlight for the lit-glass cue). Form sections in the
// collection edit flow are top-level surfaces; multiple per page so
// per-card blur was multiplied compositing pressure.
.v2-card {
  background: var(--v2-glass-surface-1);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: var(--v2-radius-lg);
  box-shadow:
    0 1px 0 0 oklch(1 0 0 / 0.1) inset,
    var(--v2-glass-shadow);
}

.v2-heading-sm {
  font-family: var(--v2-font-display);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--v2-foreground);
  margin: 0;
}

.v2-flex { display: flex; }
.v2-items-center { align-items: center; }
.v2-justify-center { justify-content: center; }
.v2-justify-between { justify-content: space-between; }
.v2-p-12 { padding: 3rem; }
.v2-mb-3 { margin-bottom: var(--v2-space-3); }
.v2-mb-4 { margin-bottom: var(--v2-space-4); }
.v2-mb-6 { margin-bottom: var(--v2-space-6); }
.v2-mt-2 { margin-top: var(--v2-space-2); }
.v2-mt-3 { margin-top: var(--v2-space-3); }

// Input styles used in textarea (matches V2Input)
.v2-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--v2-space-1);
}

.v2-input__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--v2-foreground);
}

// Page-local fallback for native <input> / <textarea> elements that use
// the bare .v2-input class instead of the V2Input Vue component. Keep the
// recipe in sync with V2Input.vue so the description textarea and date
// fields match the glass treatment of every other v2 input app-wide.
.v2-input {
  width: 100%;
  padding: var(--v2-space-2) var(--v2-space-3);
  font-family: var(--v2-font-sans);
  font-size: 0.875rem;
  color: var(--v2-foreground);
  // Translucent fill, no `backdrop-filter` — matches V2Input recipe.
  background: var(--v2-glass-surface-2);
  border: 1px solid var(--v2-glass-stroke);
  border-radius: var(--v2-radius-md);
  outline: none;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--v2-muted-foreground);
  }

  &:hover:not(:disabled):not(:focus):not([readonly]) {
    border-color: var(--v2-glass-stroke-strong);
  }

  &:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
  }

  &--error {
    border-color: var(--v2-destructive);

    &:focus {
      box-shadow: 0 0 0 3px rgba(229, 72, 77, 0.25);
    }
  }
}

.v2-input__error {
  font-size: 0.75rem;
  color: var(--v2-destructive);
}

// Responsive
@media (max-width: 768px) {
  .v2-collection-edit__grid {
    grid-template-columns: 1fr;
    grid-template-areas:
      'cover'
      'form'
      'mass'
      'catalog';
  }

  .v2-collection-edit__grid--no-catalog {
    grid-template-areas:
      'cover'
      'form'
      'mass';
  }

  .v2-collection-edit__dates-row {
    grid-template-columns: 1fr;
  }

  .v2-collection-edit__radio-group {
    flex-direction: column;
    gap: var(--v2-space-2);
  }
}

// Save island entrance: rises in when changes appear, sinks away on save.
.kp-island-enter-active,
.kp-island-leave-active {
  transition: opacity 0.22s ease, transform 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.kp-island-enter,
.kp-island-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}
</style>
